// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { useState } from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BaseOperation <ID> = (keys: ID[], ...args: any[]) => unknown;

type WrappedOperation <ID, Operation extends BaseOperation<ID>> =
    (this: unknown, ...args: Parameters<Operation>) => Promise<Awaited<ReturnType<Operation>>>;

const useListOperation = <ID> () => {
    const [selection, setSelection] = useState<ID[]>([]);
    const [loadingRows, setLoadingRows] = useState<ID[]>([]);

    const wrapListOperation = <O extends BaseOperation<ID>> (operation: O) => async function (keys, ...args) {
        setSelection(currentKeys => (
            currentKeys.filter(key => !keys.includes(key))
        ));
        setLoadingRows(currentKeys => [...currentKeys, ...keys]);

        try {
            return await operation.call(this, keys, ...args);
        } finally {
            setLoadingRows(currentKeys => (
                currentKeys.filter(key => !keys.includes(key))
            ));
        }
    } as WrappedOperation<ID, O>;

    return {
        selection,
        setSelection,
        loadingRows,
        wrapListOperation,
    } as const;
};

export default useListOperation;
